<template>
    <div ref="container" class="split-pane">
        <div ref="paneLeft" class="split-pane__pane split-pane__pane--left" :style="leftStyle">
            <slot name="left"></slot>
        </div>

        <div
            ref="splitter"
            class="split-pane__splitter"
            role="separator"
            aria-orientation="vertical"
            tabindex="0"
            @mousedown.prevent="startResize"
        >
            <div class="split-pane__splitter-handle">
                <CompassIcon icon="drag-and-drop" :size="CompassEnums.IconSize.EXTRA_SMALL" />
            </div>
        </div>

        <div class="split-pane__pane split-pane__pane--right" :style="rightStyle">
            <slot name="right"></slot>
        </div>

        <div v-if="isResizing" class="split-pane__drag-overlay"></div>
    </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, nextTick, useTemplateRef } from 'vue';
import SessionStorage from 'Core/commons/SessionStorage';

// Props
const props = defineProps({
    initialLeft: {
        type: Number,
        required: true,
    },
    leftMin: {
        type: Number,
        default: 0,
    },
    rightMin: {
        type: Number,
        default: 0,
    },
    splitterWidth: {
        type: Number,
        default: 12,
    },
    storageKey: {
        type: String,
        default: null,
    },
});

// Refs
const container = useTemplateRef('container');
const paneLeft = useTemplateRef('paneLeft');
const splitter = useTemplateRef('splitter');

const leftWidth = ref(props.initialLeft);
const startX = ref(0);
const startLeftWidth = ref(0);
const isResizing = ref(false);
const containerWidth = ref(0);
const dragRafId = ref(null);
const pendingX = ref(null);

let storage = null;

// Computed
const leftStyle = computed(() => ({
    width: `${leftWidth.value}px`,
    minWidth: `${props.leftMin}px`,
}));

const rightStyle = computed(() => ({
    minWidth: `${props.rightMin}px`,
}));

// Helpers
const getSplitterWidth = () => {
    const width = splitter.value?.offsetWidth;
    return width > 0 ? width : props.splitterWidth;
};

const clampLeftWidth = (width) => {
    const containerSize = containerWidth.value || container.value?.offsetWidth || 0;
    const maxLeft = containerSize - props.rightMin - getSplitterWidth();
    const safeMax = Math.max(props.leftMin, maxLeft);
    return Math.min(Math.max(width, props.leftMin), safeMax);
};

const updateContainerWidth = () => {
    containerWidth.value = container.value?.offsetWidth || containerWidth.value;
    leftWidth.value = clampLeftWidth(leftWidth.value);
};

const loadSavedWidth = () => {
    if (!storage) {
        return;
    }
    const saved = storage.get();
    if (saved?.width) {
        leftWidth.value = clampLeftWidth(saved.width);
    }
};

const saveWidth = () => {
    if (!storage) {
        return;
    }
    storage.set({ width: leftWidth.value });
};

const cancelDragRaf = () => {
    if (dragRafId.value) {
        cancelAnimationFrame(dragRafId.value);
        dragRafId.value = null;
    }
};

// Event handlers
const onDrag = (event) => {
    if (!isResizing.value) {
        return;
    }
    pendingX.value = event.clientX;
    if (dragRafId.value) {
        return;
    }
    dragRafId.value = requestAnimationFrame(() => {
        dragRafId.value = null;
        if (!isResizing.value || pendingX.value === null) {
            return;
        }
        const delta = pendingX.value - startX.value;
        leftWidth.value = clampLeftWidth(startLeftWidth.value + delta);
    });
};

const stopResize = () => {
    if (!isResizing.value) {
        return;
    }
    isResizing.value = false;
    pendingX.value = null;
    cancelDragRaf();
    saveWidth();
};

const startResize = (event) => {
    isResizing.value = true;
    startX.value = event.clientX;
    updateContainerWidth();
    startLeftWidth.value = paneLeft.value?.offsetWidth || leftWidth.value;
    pendingX.value = null;
    cancelDragRaf();
};

// Lifecycle
onMounted(() => {
    window.addEventListener('mousemove', onDrag);
    window.addEventListener('mouseup', stopResize);
    window.addEventListener('resize', updateContainerWidth);
    if (props.storageKey) {
        storage = new SessionStorage('split-pane', props.storageKey);
    }
    nextTick(() => {
        updateContainerWidth();
        loadSavedWidth();
        leftWidth.value = clampLeftWidth(leftWidth.value);
    });
});

onUnmounted(() => {
    window.removeEventListener('mousemove', onDrag);
    window.removeEventListener('mouseup', stopResize);
    window.removeEventListener('resize', updateContainerWidth);
    cancelDragRaf();
});
</script>

<style lang="scss" scoped>
.split-pane {
    display: flex;
    align-items: flex-start;
    width: 100%;
    position: relative;

    &__pane {
        flex: 0 0 auto;

        &--right {
            flex: 1 1 auto;
            padding-left: 18px;
        }
    }

    &__splitter {
        align-self: stretch;
        width: 2px;
        cursor: col-resize;
        display: flex;
        align-items: center;
        justify-content: center;
        background-color: var(--colors-common-border-default);
        position: relative;
        flex: 0 0 auto;
        transition: background-color ease-in-out 0.15s;

        &:hover,
        &:focus,
        &:active {
            background-color: var(--colors-common-border-primary-default);

            .split-pane__splitter-handle {
                border-color: var(--colors-common-border-primary-default);
                color: var(--colors-common-border-primary-default);
            }
        }
    }

    &__splitter-handle {
        width: 18px;
        height: 18px;
        background-color: var(--colors-common-background-default);
        position: absolute;
        transition:
            border-color ease-in-out 0.15s,
            color ease-in-out 0.15s;
        border-radius: 100%;
        border: 2px solid var(--colors-common-border-default);
        color: var(--colors-common-border-default);
        display: flex;
        justify-content: center;
        align-items: center;
    }

    &__drag-overlay {
        position: fixed;
        inset: 0;
        cursor: col-resize;
        z-index: 10;
    }
}
</style>
